import React, { ReactNode, useState } from 'react'

import { SxProps } from '@mui/material'

import type { TMetricRequirementDto } from '@homepal/api-contract'

import { useAnalysisTypeActions } from 'redux/actions/AnalysisTypes'
import { useAnalysisType } from 'redux/hooks/AnalysisTypes'
import { useAccountSettings } from 'redux/hooks/SignedInAccount'
import { periodIsUnmet } from 'utils/analysis'
import { useTranslation } from 'language/translator'

import Box from 'components_new/atoms/Box'
import Tooltip from 'components_new/atoms/Tooltip'

import EmptyPlaceholder from 'components_new/molecules/EmptyPlaceholder'
import NavigationItem from 'components_new/molecules/NavigationItem'
import SectionHeader from 'components_new/molecules/SectionHeader'

import ConfigureSlicesDialog, {
  TConfigureSlice
} from 'components_new/organisms/dialogs/ConfigureSlicesDialog'

const ECONOMY_SETTINGS_PATH = '/settings/economy'

interface AnalysisTypeDetailsProps {
  id: string
  sx?: SxProps
}

const RequirementSection = ({
  children,
  title,
  tooltipBody
}: {
  children: ReactNode
  title: string
  tooltipBody: string
}) => (
  <Box sx={{ mb: 2 }}>
    <SectionHeader
      gutterBottom={true}
      iconTip={{
        size: 'xsmall',
        title: (
          <>
            <u>{title}</u> {tooltipBody}
          </>
        )
      }}
      size="small"
      title={title}
    />
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      {children}
    </Box>
  </Box>
)

const RequirementItem = ({
  disabled = false,
  href,
  icon,
  met,
  onClick,
  title,
  titlePrefix
}: {
  disabled?: boolean
  href?: string
  icon: 'Metric' | 'Settings' | 'Slice'
  met: boolean
  onClick?: () => void
  title: string
  titlePrefix?: string
}) => {
  const { t } = useTranslation()

  return (
    <Tooltip
      title={
        disabled ? t('analysis_types', 'configured_by_admin_tooltip') : null
      }
    >
      <Box>
        <NavigationItem
          arrow={!disabled && Boolean(onClick)}
          border={true}
          color={met ? 'primary' : 'disabled'}
          external={!disabled && Boolean(href)}
          item={{
            disabled: disabled,
            endIcon: {
              color: met ? 'success' : 'error',
              name: met ? 'Check' : 'Cancel'
            },
            href: disabled ? undefined : href,
            icon: { name: icon },
            onClick: disabled ? undefined : onClick,
            title: title,
            titlePrefix: titlePrefix
          }}
        />
      </Box>
    </Tooltip>
  )
}

const AnalysisTypeDetails = (props: AnalysisTypeDetailsProps) => {
  const { id, sx } = props

  const { analysisType } = useAnalysisType(id)
  const { isAdmin, isEconomist } = useAccountSettings()
  const { tryRefetchAllAnalysisTypes } = useAnalysisTypeActions()
  const { t } = useTranslation()

  const [configureSlice, setConfigureSlice] = useState<TConfigureSlice | null>(
    null
  )

  const handleCloseConfigureSlice = (changed: boolean) => {
    if (changed) {
      tryRefetchAllAnalysisTypes()
    }

    setConfigureSlice(null)
  }

  if (!analysisType) return null

  const {
    closed_accounting_period: period,
    metrics,
    slices
  } = analysisType.data_requirements

  if (metrics.length === 0 && slices.length === 0 && !period.required) {
    return (
      <EmptyPlaceholder
        size="xsmall"
        title={t('analysis_types', 'no_requirements_title')}
      />
    )
  }

  return (
    <Box sx={sx}>
      {metrics.length > 0 ? (
        <RequirementSection
          title={t('analysis_types', 'required_metrics_title')}
          tooltipBody={t('analysis_types', 'required_tooltip_body')}
        >
          {metrics.map((item: TMetricRequirementDto) => (
            <RequirementItem
              href={`/metrics/${item.id}`}
              icon="Metric"
              key={item.id}
              met={item.met}
              title={item.name}
            />
          ))}
        </RequirementSection>
      ) : null}

      {slices.length > 0 ? (
        <RequirementSection
          title={t('analysis_types', 'required_slices_title')}
          tooltipBody={t('analysis_types', 'required_tooltip_body')}
        >
          {slices.map((item) => {
            const entityId = item.entity_id

            return (
              <RequirementItem
                disabled={!isAdmin}
                icon="Slice"
                key={item.id}
                met={item.met}
                onClick={
                  entityId
                    ? () =>
                        setConfigureSlice({
                          configured: item.met,
                          entity_id: entityId,
                          id: item.id
                        })
                    : undefined
                }
                title={item.name}
                titlePrefix={item.entity_title ?? undefined}
              />
            )
          })}
        </RequirementSection>
      ) : null}

      {period.required ? (
        <RequirementSection
          title={t('analysis_types', 'required_period_title')}
          tooltipBody={t('analysis_types', 'required_period_tooltip_body')}
        >
          <RequirementItem
            disabled={!isAdmin && !isEconomist}
            href={ECONOMY_SETTINGS_PATH}
            icon="Settings"
            met={!periodIsUnmet(analysisType)}
            title={t('analysis_types', 'closed_period_name')}
          />
        </RequirementSection>
      ) : null}

      <ConfigureSlicesDialog
        helperText={t('analysis_types', 'required_slices_dialog_helper')}
        onClose={handleCloseConfigureSlice}
        open={configureSlice !== null}
        slices={configureSlice ? [configureSlice] : []}
      />
    </Box>
  )
}

export default AnalysisTypeDetails
